import styled from '@emotion/styled'
import DragEventService, { EventPosition } from 'drag-event-service'
import { memo, useCallback, useRef } from 'react'
import {
    FLOATING_ROOM,
    FULLPAGE_LOCATION_STYLE_MAP,
    FULLPAGE_SIZE_STYLE_MAP,
    FloaterSize,
} from '../../../constants/floaters'
import { RoomView, setModalStack } from '../../../state/modals'
import { openHideFloatingPlayerDialog } from '../../../state/modals/state.dialog'
import {
    closeFloater,
    setFloaterSize,
    updateFloaterLocation,
} from '../../../state/modals/state.floater'
import { isTouchDevice } from '../../../utilities/browser'
import IconButton from '../../Buttons/IconButton'
import { useFloatingRoom } from '../../Hooks/useModal'
import { useRoomSelector } from '../../Hooks/useRoomAppState'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import CloseIcon from '../../Icons/Close'
import Minus from '../../Icons/Minus'
import Plus from '../../Icons/Plus'
import Settings from '../../Icons/Settings'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
import layers from '../../styles/layers'
import shape from '../../styles/shape'
import {
    COMPONENT_HEIGHTS,
    PAPER_HEIGHTS,
    getBrowserBottomInset,
} from '../../theme'
import {
    checkParent,
    type FloaterRegisterOptions,
    type LocationRef,
} from './Floater'
import FloatingRoom from './Floaters/FloatingRoom'

const FloaterRoot = styled('div')({
    position: 'absolute',
    zIndex: PAPER_HEIGHTS.floater - 1,
    opacity: 1,
    transition: 'transform 180ms ease', // is this necessary?
    transformStyle: 'preserve-3d',
    display: 'block',
})

export const PreviewCloseButton = styled(IconButton)({
    boxShadow: layers.one,
    backgroundColor: `${palette.background} !important`,
    position: 'absolute' as const,
    zIndex: PAPER_HEIGHTS.floater,
    border: palette.layerLightBorder,
})

const SideButtonContainer = styled('div')({
    position: 'absolute',
    zIndex: PAPER_HEIGHTS.floater,
    display: 'flex',
    flexFlow: 'column nowrap',
})

export const SizeButton = styled(IconButton)({
    boxShadow: layers.one,
    backgroundColor: `${palette.background} !important`,
    marginTop: getThemeSpacing(0.5),
    border: palette.layerLightBorder,
})

const BUTTON_ROOT_LOCATION_MAP = {
    [FloaterSize.Fullscreen]: [],
    [FloaterSize.Small]: [-28, -28],
    [FloaterSize.Medium]: [-24, -28],
    [FloaterSize.Large]: [-28, -28],
}

type FullscreenFloaterProps = {
    vertical: boolean
    fullWindow: boolean
    isMobile: boolean
}

const getSafeOrientation = (
    isMobile: boolean,
    vertical: boolean,
    fullPage: boolean,
) => {
    if (isMobile) {
        return vertical ? 'portrait' : 'landscape'
    }
    if (fullPage) {
        return vertical ? 'portrait' : 'landscape'
    }
    return 'landscape'
}

const checkEarlyReturn = (viewing: boolean, size: FloaterSize, pm: boolean) => {
    if (size < FloaterSize.Fullscreen) {
        if (!viewing && !pm) return true
    }
    return false
}

const FullscreenFloater = memo((props: FullscreenFloaterProps) => {
    const container = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const growRef = useRef<HTMLButtonElement>(null)
    const shrinkRef = useRef<HTMLButtonElement>(null)
    const settingsRef = useRef<HTMLButtonElement>(null)
    const eventsRef = useRef<FloaterRegisterOptions>(null)
    const location = useRef<LocationRef>({
        originX: 0, // move origin
        originY: 0,
        pointerX: 0, // mouse pointer
        pointerY: 0,
        bottomInset: 0,
    })
    const { isMobile, vertical: appVertical } = props
    const [floater, stackPosition] = useFloatingRoom()
    const roomWidth = useScreenStateSelector('roomWidth')
    const roomHeight = useScreenStateSelector('roomHeight')
    const roomX = useScreenStateSelector('roomX')
    const roomY = useScreenStateSelector('roomY')
    const contentWidth = useScreenStateSelector('contentWidth')
    const contentHeight = useScreenStateSelector('contentHeight')
    const orientationType = useScreenStateSelector('orientationType')
    const { state: floaterProps, show } = floater
    // console.log('[FullscreenFloater]', floaterProps, props, {
    //     roomWidth,
    //     roomHeight,
    // })
    const {
        // active,
        locations: startingLocations,
        // fullScreenLocation,
        expanded,
        size,
        minimized,
        roomView,
        id, //: referenceID,
    } = floaterProps

    const viewing = useRoomSelector(id, 'viewing')

    const fullPage = size === FloaterSize.Fullscreen
    const orientation = getSafeOrientation(
        isMobile || isTouchDevice,
        appVertical,
        fullPage,
    )
    const orientedLocation = fullPage
        ? expanded
            ? { width: roomWidth, height: roomHeight }
            : { width: contentWidth, height: contentHeight }
        : startingLocations[orientation]
    const vertical = orientedLocation.height > orientedLocation.width
    // const { id } = floaterProps
    // const shouldLimitMaxX = close || stickyHeader

    const decreaseFloaterSize = useCallback(() => {
        if (size > FloaterSize.Small) {
            setFloaterSize(id, FLOATING_ROOM, size - 1)
        }
    }, [size, id])
    const increaseFloaterSize = useCallback(() => {
        if (size < FloaterSize.Fullscreen) {
            setFloaterSize(id, FLOATING_ROOM, size + 1)
        }
    }, [size, id])

    const onDrag = useCallback(
        (event: MouseEvent | TouchEvent, position: EventPosition) => {
            event.stopPropagation()
            event.preventDefault()
            if (!id) return

            const diffX = location.current.pointerX - position.pageX
            const diffY = location.current.pointerY - position.pageY
            location.current.pointerX = position.pageX
            location.current.pointerY = position.pageY

            const node = container.current
            if (!node) return

            const left = node.offsetLeft - diffX
            const top = node.offsetTop - diffY
            const height = document.body.clientHeight - node.offsetHeight
            const width = document.body.clientWidth - node.offsetWidth

            // check if we should limit max X or not
            const fixedX = Math.min(Math.max(8, left), width - 36)
            const fixedY = Math.min(
                Math.max(8, top),
                height -
                    (appVertical
                        ? 8 +
                          COMPONENT_HEIGHTS.navBottom +
                          location.current.bottomInset
                        : 8),
            )
            node.style.left = fixedX + 'px'
            node.style.top = fixedY + 'px'
            node.style.cursor = 'grabbing'
            // console.log('[Floater->onDrag]', idToSlug[id], { fixedX, fixedY })
            updateFloaterLocation(id, FLOATING_ROOM, {
                x: fixedX,
                y: fixedY,
            })
        },
        [id, appVertical],
    )

    const onDragEnd = useCallback(
        (event: MouseEvent | TouchEvent) => {
            event.stopPropagation()
            event.preventDefault()
            if (!id) return
            if (
                Math.abs(
                    location.current.pointerX - location.current.originX,
                ) <= 3 &&
                Math.abs(
                    location.current.pointerY - location.current.originY,
                ) <= 3
            ) {
                const target = event.target as HTMLElement
                //touch/mobile: need to check if the target is one of the buttons (grow, shrink, or close)
                if (checkParent(target, closeRef.current)) {
                    onClose()
                }
                if (checkParent(target, growRef.current)) {
                    increaseFloaterSize()
                }
                if (checkParent(target, shrinkRef.current)) {
                    decreaseFloaterSize()
                }
                if (checkParent(target, settingsRef.current)) {
                    openHideFloatingPlayerDialog()
                }
                if (eventsRef.current?.onCheck && eventsRef.current?.onAction) {
                    const [passed, data] = eventsRef.current.onCheck(
                        event,
                        false,
                        {
                            locations: startingLocations,
                        },
                    )
                    if (passed) {
                        eventsRef.current.onAction(
                            event,
                            false,
                            {
                                locations: startingLocations,
                            },
                            data,
                        )
                    }
                }
            }
            DragEventService.off(document, 'end', onDragEnd)
            DragEventService.off(document, 'move', onDrag)
        },
        [
            id,
            startingLocations,
            onDrag,
            decreaseFloaterSize,
            increaseFloaterSize,
        ],
    )

    const onDragStart = useCallback(
        (event: MouseEvent | TouchEvent, position: EventPosition) => {
            event.stopPropagation()
            event.preventDefault()
            if (!id) return
            setModalStack(FLOATING_ROOM)
            // eslint-disable-next-line fp/no-let
            let shouldSubscribe = true
            if (eventsRef.current?.onCheck && eventsRef.current?.onAction) {
                const [passed, data, skipDrag] = eventsRef.current.onCheck(
                    event,
                    true,
                    {
                        locations: startingLocations,
                    },
                )
                shouldSubscribe = !skipDrag

                if (passed) {
                    eventsRef.current.onAction(
                        event,
                        true,
                        { locations: startingLocations },
                        data,
                    )
                }
            }
            location.current.originX = location.current.pointerX =
                position.pageX
            location.current.originY = location.current.pointerY =
                position.pageY
            location.current.bottomInset = getBrowserBottomInset()

            if (shouldSubscribe) {
                DragEventService.on(document, 'end', onDragEnd, {
                    touchArgs: [{ passive: false }],
                })
                DragEventService.on(document, 'move', onDrag, {
                    touchArgs: [{ passive: false }],
                })
            }
        },
        [id, onDragEnd, onDrag, startingLocations],
    )

    const containerRef = useCallback(
        (ref: HTMLDivElement) => {
            if (ref) {
                container.current = ref
                if (!fullPage) {
                    if (orientedLocation) {
                        container.current.style.left = orientedLocation.x + 'px'
                        if (typeof orientedLocation.y === 'number')
                            container.current.style.top =
                                orientedLocation.y + 'px'
                        else container.current.style.top = orientedLocation.y
                    }
                    DragEventService.on(ref, 'start', onDragStart, {
                        touchArgs: [{ passive: false }],
                    })
                } else {
                    container.current.style.left = roomX + 'px'
                    container.current.style.top = roomY + 'px'
                }
            } else {
                if (container.current) {
                    DragEventService.off(
                        container.current,
                        'start',
                        onDragStart,
                    )
                }
            }
        },
        [id, onDragStart, fullPage, orientedLocation],
    )
    // provide a way for the modal to move itself if needed
    // const moveFloater = useCallback(
    //     (x: number, y: number) => {
    //         if (container && container.current) {
    //             const node = container.current
    //             node.style.left = x + 'px'
    //             node.style.top = y + 'px'
    //         }
    //         updateFloaterLocation(id, FLOATING_ROOM, {
    //             x,
    //             y,
    //         })
    //     },
    //     [id],
    // )

    const onClose = useCallback(() => closeFloater(id, FLOATING_ROOM), [id])

    if (checkEarlyReturn(viewing, size, roomView === RoomView.PM)) {
        return null
    }
    // console.log(
    //     '[FullscreenFloater] rendering',
    //     id,
    //     {
    //         // show,
    //         // size,
    //         fullPage,
    //         orientation,
    //         expanded,
    //         vertical,
    //     },
    //     orientedLocation,
    // )
    const [topRoot, rightRoot] = BUTTON_ROOT_LOCATION_MAP[size]
    const fullPageLocationStyles = FULLPAGE_LOCATION_STYLE_MAP[orientationType]
    // leverage css calcs if the room is full sized as safari plays nicer on rotations
    // when we don't have the room sized by fixed pixel values
    const fullPageSizeStyles = FULLPAGE_SIZE_STYLE_MAP[orientationType]
    const shouldSizeWithCss = fullPage && expanded
    return (
        <FloaterRoot
            css={[
                fullPageLocationStyles,
                shouldSizeWithCss ? fullPageSizeStyles : null,
            ]}
            ref={containerRef}
            style={{
                zIndex: PAPER_HEIGHTS.floater - stackPosition,
                borderRadius: fullPage ? 0 : shape.borderRadius,
                boxShadow: !fullPage && show ? layers.two : 'none',
                width: !shouldSizeWithCss ? orientedLocation.width : null,
                height: !shouldSizeWithCss ? orientedLocation.height : null,
                containerName: 'room-container',
                containerType: 'inline-size',
                border: !fullPage ? palette.layerLightBorder : null,
            }}
            data-testid="Container__Floater"
        >
            <FloatingRoom
                id={id}
                size={size}
                roomView={roomView}
                minimized={minimized}
                vertical={vertical}
                isMobile={isMobile}
                show={show}
                ref={eventsRef}
                width={orientedLocation.width}
                height={orientedLocation.height}
            />
            {show && !fullPage && (
                <PreviewCloseButton
                    data-testid="PreviewModal__CloseButton"
                    style={{
                        right: rightRoot,
                        top: topRoot,
                    }}
                    title="Close Preview"
                    ref={closeRef}
                >
                    <CloseIcon />
                </PreviewCloseButton>
            )}
            {show && !fullPage && (
                <SideButtonContainer
                    style={{
                        right: rightRoot,
                        top: topRoot + 38,
                    }}
                >
                    {size < FloaterSize.Large && (
                        <SizeButton
                            data-testid={'PreviewModal__PlusButton'}
                            ref={growRef}
                        >
                            <Plus title="Embiggen Preview" size={0.5} />
                        </SizeButton>
                    )}
                    {size > FloaterSize.Small && (
                        <SizeButton
                            data-testid="PreviewModal__MinusButton"
                            ref={shrinkRef}
                        >
                            <Minus title="Shrink Preview" size={0.5} />
                        </SizeButton>
                    )}
                    {show && (
                        <SizeButton
                            data-testid="PreviewModal__HideFloatingPlayer"
                            ref={settingsRef}
                        >
                            <Settings
                                title="Disable Floating Player"
                                size={0.5}
                            />
                        </SizeButton>
                    )}
                </SideButtonContainer>
            )}
        </FloaterRoot>
    )
})

export default FullscreenFloater
